import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { NEVER, of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { fadeIn, fadeOut, slideDown, slideUp } from 'src/app/animations';
import { AuthService } from 'src/app/services/auth.service';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { SiteNotesService } from 'src/app/services/sections/site-notes/site-notes.service';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { ToastService } from 'src/app/services/ui/toast.service';
import { ButtonIcons } from '../../action-button/models/button-icons.enum';
import { ToastType } from '../../toast/models/toast-type.enum';
import { Document } from '../../../models/document.model';
import { DocumentsService } from 'src/app/services/sections/documents/documents.service';
import { MatSidenav } from '@angular/material/sidenav';
import { DocViewerDocumentDetails } from '../../document-viewer/document-viewer.component';
import { LoginRedirectPage } from '../../login-wizard/models/login-redirect-page.enum';
import { AccountsService } from 'src/app/services/ui/accounts.service';
import { DeficiencyItem } from 'src/app/models/deficiencies/deficiency-item.model';

@Component({
	selector: 'deficiency-view',
	templateUrl: './deficiency-view.component.html',
	styleUrls: ['../../stylesheets/job-actions.scss', '../../stylesheets/job-main.scss', '../../stylesheets/document-viewer.scss', '../../stylesheets/job-inputs.scss', './deficiency-view.component.scss'],
	animations: [slideUp(100), slideDown(100), fadeIn(200), fadeOut(200)]
})
export class DeficiencyViewComponent implements AfterViewInit, OnDestroy {

	@ViewChild('sidenav', { static: true }) public sidenav: MatSidenav;

	public self = this;

	public isLoading = false;
	public isVendor = false;
	public isEditMode = false;
	public isDocumentsLoading = false;
	public isOpened = false;
	public viewEmail = false;
	public viewDocument = false;

	public deficiencyFormGroup: FormGroup;
	public deficiencyBackup: any;

	public deficiency: DeficiencyItem;

	public sites: { name: string, siteId: number }[] = [];
	public deficiencyClasses: { name: string, classId: number }[] = [];
	public deficiencyTypes: { name: string, typeId: number }[] = [];
	public deficiencySubtypes: { name: string, subtypeId: number }[] = [];
	public possibleAssignees: { name: string, assigneeId: number }[] = [];

	public documents: Document[] = [];
	public selectedDocumentDetails: DocViewerDocumentDetails;

	public documentLoadUnsubscriber$ = new Subject<void>();

	public readonly ButtonIcons = ButtonIcons;
	public readonly moment = moment;
	public readonly location = location;

	constructor(
		private route: ActivatedRoute,
		private authService: AuthService,
		private router: Router,
		private siteNotesService: SiteNotesService,
		private documentsService: DocumentsService,
		private accountsService: AccountsService
	) {
		this.deficiency = this.route.snapshot.data.deficiency;

		this.sites = this.route.snapshot.data.sites || [];
		this.deficiencyClasses = this.route.snapshot.data.deficiencyClasses || [];
		this.possibleAssignees = this.route.snapshot.data.possibleAssignees || [];

		GlobalSettingsService.navigationBarActions.set(
			'deficiencies-access-button',
			{
				icon: 'deficiencies',
				listOrder: 1,
				isCustomSvgIcon: true,
				action: () => this.accountsService.redirectToUserAccountPage(LoginRedirectPage.Deficiencies, { isAutoAccountRedirect: true }).subscribe()
			}
		);

		this.deficiencyFormGroup = new FormGroup({
			deficiencyRequestId: new FormControl(this.deficiency.deficiencyRequestId),
			siteObj: new FormControl(this.sites.find(item => item.siteId === this.deficiency.siteId), [Validators.required]),
			classObj: new FormControl(this.deficiencyClasses.find(item => item.classId === this.deficiency.classId), [Validators.required]),
			typeObj: new FormControl(this.deficiencyTypes.find(item => item.typeId === this.deficiency.typeId), [Validators.required]),
			subtypeObj: new FormControl(this.deficiencySubtypes.find(item => item.subtypeId === this.deficiency.subTypeId), [Validators.required]),
			standardClassObj: new FormControl(this.deficiency.standardClassId ? this.deficiencyClasses.find(item => item.classId === this.deficiency.standardClassId) : null),
			standardTypeObj: new FormControl(this.deficiency.standardTypeId ? this.deficiencyTypes.find(item => item.typeId === this.deficiency.standardTypeId) : null),

			floor: new FormControl(this.deficiency.floor),
			room: new FormControl(this.deficiency.room),
			location: new FormControl(this.deficiency.location),
			isRequestedAsap: new FormControl(!!this.deficiency.isRequestedAsap),
			requestedOn: new FormControl(this.deficiency.requestedOn),
			isEmergency: new FormControl(!!this.deficiency.isEmergency),
			comment: new FormControl(this.deficiency.comment),

			// isTest: new FormControl(this.deficiency.)
		});

		this.deficiencyFormGroup.patchValue({
			...this.deficiency,
			// siteObj: this.sites.find(item => item.siteId === this.deficiency.siteId),
			// siteNoteCategoryObj: this.deficiencyClasses.find(item => item.classId === this.deficiency.classId),
			// assignedUserObj: this.possibleAssignees.find(item => item.assigneeId === this.siteNote.assignedUserId)
		});

		this.deficiencyBackup = this.deficiencyFormGroup.getRawValue();

		this.deficiencyFormGroup.disable();

		if (this.route.snapshot.queryParamMap.get('presetEdit')) {
			this.startEditMode();
		}

		this.populateDocumentsList();
	}

	ngAfterViewInit(): void {
		setTimeout(() => {
			GlobalSettingsService.isGlobalLoaderLoading = false;
			GlobalSettingsService.isSecondaryLoaderLoading = false;
		}, 0);
	}

	public ngOnDestroy(): void {
		this.documentLoadUnsubscriber$.next();
		this.documentLoadUnsubscriber$.complete();
	}

	public toggleSlide(): void {
		this.isOpened = !this.isOpened;
		this.sidenav.toggle();
	}

	public populateDocumentsList(): void {
		if (!this.deficiency?.photosUrlHash || !this.deficiency.photosCount) {
			this.documents = [];
		} else {
			this.isDocumentsLoading = true;

			this.documentsService.getDocumentSummary(this.deficiency.photosUrlHash)
				.subscribe(result => {
					this.documents = result || [];
					this.isDocumentsLoading = false;
				});
		}
	}

	public updateDeficiency(): void {
		const model = this.deficiencyFormGroup.getRawValue();
		this.deficiencyFormGroup.disable();

		model.siteId = model.site.siteId;
		model.siteNoteCategoryId = model.siteNoteCategory.categoryId;
		model.assignedToId = model.assignedTo.assigneeId;

		// this.siteNotesService.updateSiteNote(TokenHelper.getToken(this.route), model)
		// 	.pipe(catchError(() => {
		// 		ToastService.pushToast({
		// 			text: 'Could not update site note. Please try again!',
		// 			icon: 'error',
		// 			type: ToastType.Error
		// 		});

		// 		this.deficiencyFormGroup.enable();

		// 		return NEVER;
		// 	}))
		// 	.subscribe(result => {
		// 		this.deficiencyBackup = this.deficiencyFormGroup.getRawValue();
		// 		this.stopEditMode();
		// 	});

		// console.log(model);

	}

	public startEditMode(): void {

		// Check if user is authenticated

		if (!this.authService.isLoggedIn()) {
			localStorage.setItem('rurl', location.href + '?presetEdit=true');
			this.authService.login();
		} else {
			this.isEditMode = true;
			this.deficiencyFormGroup.enable();
		}

	}

	public stopEditMode(): void {
		this.deficiencyFormGroup.patchValue(this.deficiencyBackup);
		this.isEditMode = false;
		this.deficiencyFormGroup.disable();
	}

	public showDocument(document: Document): void {
		this.selectedDocumentDetails = null;
		this.viewEmail = false;
		this.viewDocument = true;
		this.documentLoadUnsubscriber$.next();

		if (!this.isOpened) {
			this.toggleSlide();
		}

		this.documentsService
			.getDocument(this.deficiency.photosUrlHash, document.documentId)
			.pipe(
				takeUntil(this.documentLoadUnsubscriber$)
			)
			.subscribe((content) => {
				this.selectedDocumentDetails = {
					documentId: document.documentId,
					content,
					documentDisplayName: document.name,
					documentDownloadName: document.name
				};
			});
	}

	public triggerCopyToast(): void {
		ToastService.pushToast({
			text: 'Link copied to clipboard!',
			icon: 'done',
			type: ToastType.Success
		});
	}
}
